/**
 * **The edit page shell** (W28, FR-65, plan D84) — copy-adapted from ProDesk's
 * (`repo:prodesk:apps/web/src/components/edit/EditPage.tsx`), whose settings
 * the owner asked ProDoc's to follow.
 *
 * A back link, a title, one line of what the page is for, and the page's
 * content in a column wide enough for a two-column form and no wider. A page
 * that edits puts its `EditBar` last among its children, so the bar sticks to
 * the bottom of this column and spans exactly its width.
 *
 *     <EditPage title="Retention" description="…" back={{ to: '/settings', label: 'Settings' }}>
 *       {guard}
 *       …sections…
 *       <EditBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} />
 *     </EditPage>
 */
import { PageHeader } from '@fiveox/ui'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

export interface EditPageBack {
  /** Where the back link goes — `/settings`. */
  to: string
  /** What it reads — *Settings*. */
  label: string
}

export interface EditPageProps {
  title: ReactNode
  description?: ReactNode
  back: EditPageBack
  children: ReactNode
  /** Secondary actions at the top right — never a second Save. */
  actions?: ReactNode
  /** Under the title: a badge, a status line. */
  meta?: ReactNode
}

export function EditPage({ title, description, back, children, actions, meta }: EditPageProps) {
  return (
    <div data-edit-page="" className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        back={
          <Link to={back.to} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium">
            <ArrowLeft size={14} aria-hidden /> {back.label}
          </Link>
        }
        title={title}
        description={description}
        actions={actions}
        meta={meta}
      />
      {children}
    </div>
  )
}
